import { useCallback, useEffect, useMemo, useState } from 'react'
import { getFans, getModels } from '../../api'
import { asList, isPositiveInt, parseOptionalInt } from './helpers'

function toBool(value) {
  if (value === true || value === 1) return true
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase()
    return v === 'true' || v === '1' || v === 'yes'
  }
  return false
}

function isTestModel(model) {
  return toBool(
    model?.is_test
    ?? model?.isTest
    ?? model?.is_test_model
    ?? model?.test_model
    ?? model?.test
    ?? model?.credentials?.is_test
    ?? model?.account?.is_test
    ?? model?.profile?.is_test,
  )
}

function hasExplicitTestFlag(model) {
  return (
    model?.is_test !== undefined
    || model?.isTest !== undefined
    || model?.is_test_model !== undefined
    || model?.test_model !== undefined
    || model?.test !== undefined
    || model?.credentials?.is_test !== undefined
    || model?.account?.is_test !== undefined
    || model?.profile?.is_test !== undefined
  )
}

export function useAutotestModels(t) {
  const [models, setModels] = useState([])

  const loadModels = useCallback(() => {
    let cancelled = false
    getModels()
      .then((res) => {
        if (cancelled) return
        const list = Array.isArray(res) ? res : asList(res)
        setModels(list)
      })
      .catch(() => setModels([]))
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    const cleanup = loadModels()
    return cleanup
  }, [loadModels])

  useEffect(() => {
    const onFocus = () => {
      loadModels()
    }
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') loadModels()
    }
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [loadModels])

  const modelOptions = useMemo(() => {
    const list = Array.isArray(models) ? models : []
    return [{ value: '', label: t('common.notSelected') }, ...list.map((m) => ({
      value: String(m.id),
      label: m.of_email || m.name || `#${m.id}`,
    }))]  }, [models, t])

  return { models, modelOptions, reloadModels: loadModels }
}

export function useAutotestFans(modelId, t) {
  const [fans, setFans] = useState([])

  useEffect(() => {
    const parsedModelId = Number(modelId)
    if (!isPositiveInt(parsedModelId)) {
      setFans([])
      return
    }
    let cancelled = false
    getFans({ modelId: parsedModelId, limit: 100, offset: 0 })
      .then((res) => {
        if (cancelled) return
        setFans(asList(res))
      })
      .catch(() => setFans([]))

    return () => {
      cancelled = true
    }
  }, [modelId])

  const fanOptions = useMemo(() => {
    const list = Array.isArray(fans) ? fans : []
    return [{ value: '', label: t('common.notSelected') }, ...list.map((f) => ({
      value: String(f.id),
      label: f.username || f.name || `#${f.id}`,
    }))]
  }, [fans, t])

  return { fans, fanOptions }
}

export function validateAutotestRunForm(runForm, t) {
  const modelId = Number(runForm.model_id)
  const fanId = Number(runForm.fan_id)
  if (!isPositiveInt(modelId) || !isPositiveInt(fanId)) {
    return t('autotests.validation.modelFanRequired')
  }

  const rounds = parseOptionalInt(runForm.max_rounds)
  if (rounds === null || (rounds != null && rounds < 1)) {
    return t('autotests.validation.maxRounds')
  }

  const timeout = parseOptionalInt(runForm.round_timeout_seconds)
  if (timeout === null || (timeout != null && timeout < 10)) {
    return t('autotests.validation.timeout')
  }

  return ''
}
